import { useAuth } from '@agentnet/auth';
import {
  AgentNetButton,
  CellLink,
  CellMessage,
  CellStatus,
  Content,
  DashboardMetricCard,
  DataTable,
  PageHeader,
  palette,
} from '@fluentsms/agentnet-web-components';
import { FormControlLabel, Grid, Switch, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  CellClickedEvent,
  ColDef,
  FilterChangedEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { AgGridReactProps } from 'ag-grid-react';
import { getFirmDetails } from 'api/onBehalfOf/api';
import { getAllOrdersApi, getLockboxNumberApi } from 'api/orderManagement/order-management-api';
import { format as formatDate } from 'date-fns';
import { KBLinks } from 'features/constants';
import { filterParams, numberValueFormatter } from 'features/Remittance/reportsListConfig';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import useSessionStorage from 'hooks/useSessionStorage';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import NoResults from 'ui-kit/components/noResults/NoResults';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { LockboxIcon } from 'ui-kit/icons/OrderManagement';
import { AgentNetTextInput } from 'ui-kit/inputs';
import AgentNetDropdownSelector from 'ui-kit/inputs/AgentNetDropdownSelector';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import states from 'ui-kit/inputs/StateSelectField/states.json';
import { dateTooltipValue } from 'utilities/utilities';
import { ConversationDrawer } from './ConversationDrawer';
import { AllOrdersStatusColor, getMemberNames } from './enum';
import { SearchFields, orderStatusOptions, validateSearchCombination } from './orderManagement.validation';
import { AllOrdersItemType, AllOrdersRequestType, GetNotesActionLog, LockboxNumberType, OrderType } from './types';

const dataQaPrefix = 'OrderManagement-AllOrders';
const stateOptions = [
  { name: 'All States', value: '' },
  ...states.map((s) => ({ name: s.name, value: s.abbreviation })),
];

export interface ReportsListInterface {
  contentClassName?: string;
}

export const dateFilters = [
  { name: '', value: '' },
  { name: 'Today', value: 'today' },
  { name: 'Yesterday', value: 'yesterday' },
  { name: 'This Week', value: 'thisWeek' },
  { name: 'Last Week', value: 'lastWeek' },
  { name: 'This Month', value: 'thisMonth' },
  { name: 'Last Month', value: 'lastMonth' },
  { name: 'Last 7 Days', value: 'last7Days' },
  { name: 'Last 30 Days', value: 'last30Days' },
  { name: 'Last 90 Days', value: 'last90Days' },
  { name: 'Custom', value: 'custom' },
];

interface DashboardProps {
  pageHeader?: string;
  useHeaderDivider?: boolean;
  showHeaderElipsis?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .ag-center-cols-viewport': {
        minHeight: 220,
      },
    },
    topFilter: {
      width: theme.spacing(2),
    },
    topFilters: {
      height: theme.spacing(2),
    },
    noRows: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 220,
      justifyContent: 'center',
    },
    filterIcon: {
      fontSize: 24,
    },
  }),
);

const CustomOverlayLoading = memo(function CustomOverlayLoading() {
  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 220,
        justifyContent: 'center',
      }}
    >
      <LoadingSpinner status="pending" className="files-container--spinner-root" />
    </div>
  );
});

const CustomOverlayNoRows = memo(function CustomOverlayNoRows() {
  return (
    <NoResults
      showButton={false}
      type="results"
      titleText="No orders match your search criteria."
      pText="Try adjusting your search filters to find what you're looking for."
    />
  );
});

interface OrderResultsPanelProps {
  gridConfig: AgGridReactProps;
  orders: AllOrdersItemType[];
  totalFiles: number;
  totalNet: number;
  displayedRowCount: number;
}

const OrderResultsPanel = memo(function OrderResultsPanel({
  gridConfig,
  orders,
  totalFiles,
  totalNet,
  displayedRowCount,
}: OrderResultsPanelProps) {
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <DashboardMetricCard
            dataTestId={`${dataQaPrefix}-Card-Orders`}
            heading="Orders"
            value={String(orders.length)}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <DashboardMetricCard dataTestId={`${dataQaPrefix}-Card-Files`} heading="Files" value={String(totalFiles)} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <DashboardMetricCard
            dataTestId={`${dataQaPrefix}-Card-Net`}
            heading="Order Net Total"
            value={String(numberValueFormatter(totalNet, true))}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DataTable {...gridConfig} fixedHeader fixedBottomScroll />
          <Typography variant="body2">
            Showing {displayedRowCount || orders.length} of {orders.length} orders
          </Typography>
        </Grid>
      </Grid>
    </>
  );
});

const colorsByStatus = (() => {
  const colors: { [status: string]: string } = {};
  const statusColors = getMemberNames(AllOrdersStatusColor);
  let statusColor = statusColors.next();
  while (!statusColor.done) {
    const colorCode = AllOrdersStatusColor[statusColor.value as keyof typeof AllOrdersStatusColor];
    colors[statusColor.value] = '#' + colorCode.toString(16).padStart(6, '0');
    statusColor = statusColors.next();
  }
  colors['Book Error'] = palette.error.main;
  return colors;
})();

const Dashboard = ({ pageHeader, useHeaderDivider, showHeaderElipsis }: DashboardProps) => {
  const classes = useStyles();
  const history = useHistory();
  const gridApiRef = useRef<GridApi<AllOrdersItemType> | null>(null);
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};

  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const { addSnackbarMessage } = useSnackBars();
  const { setUserFirm } = profileCtx;

  const [lockbox, setLockbox] = useSessionStorage('lockboxData');
  const [bookedOrder, setBookedOrder] = useSessionStorage('bookOrderData');
  const [savedFilters, setSavedFilters] = useSessionStorage('savedFilters');
  const [dashboardSessionColumnDefs, setDashboardSessionColumnDefs] = useSessionStorage('dashboardSessionColumnDefs');
  const [dashboardSessionFilterModel, setDashboardSessionFilterModel] = useSessionStorage(
    'dashboardSessionGridFilterModel',
  );

  const [lockboxList, setLockboxList] = useState<Array<LockboxNumberType>>([]);
  const [lockboxFilter, setLockboxFilter] = useState<number | null>(null);
  const [allOrders, setAllOrders] = useState<Array<AllOrdersItemType>>([]);
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [totalNet, setTotalNet] = useState<number>(0);
  const [isDashboardMessagePanelOpen, setIsDashboardMessagePanelOpen] = useState<boolean>(false);
  const [conversationPayload, setConversationPayload] = useState<GetNotesActionLog | undefined>(undefined);
  const [orderId, setOrderId] = useState<number | undefined>(undefined);
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [policyNumber, setPolicyNumber] = useState<string>('');
  const [fileNumber, setFileNumber] = useState<string>('');
  const [accountNumber, setAccountNumber] = useState<number | undefined>(undefined);
  const [orderStatus, setOrderStatus] = useState<number | undefined>(undefined);
  const [fromOrderDate, setFromOrderDate] = useState<string>('');
  const [toOrderDate, setToOrderDate] = useState<string>('');
  const [stateCode, setStateCode] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [batchReferenceNumber, setBatchReferenceNumber] = useState<string>('');
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [advancedSearch, setAdvancedSearch] = useSessionStorage('useAdvancedSearch');

  const isSearchValid = validateSearchCombination({
    orderId,
    orderNumber,
    policyNumber,
    fileNumber,
    invoiceNumber,
    batchReferenceNumber,
    accountNumber,
    lockboxId: lockboxFilter,
    orderStatus,
    fromOrderDate,
    toOrderDate,
    stateCode,
  }).valid;

  const onGridReady = (params: GridReadyEvent<AllOrdersItemType>) => {
    gridApiRef.current = params.api;
    params.api.setFilterModel(dashboardSessionFilterModel);
  };

  const onFilterChanged = (e: FilterChangedEvent): void => {
    setDisplayedRowCount(getDisplayedRowCount());
    setDashboardSessionFilterModel(e.api.getFilterModel());
  };

  const getDisplayedRowCount = () => {
    if (gridApiRef.current) return gridApiRef.current.getDisplayedRowCount();
    return 0;
  };

  const getAllOrders = async (fields: SearchFields): Promise<Array<AllOrdersItemType>> => {
    const payload: AllOrdersRequestType = {
      fromOrderDate: fields.fromOrderDate || '01/01/1900',
      toOrderDate: fields.toOrderDate || '12/31/2099',
      lockboxId: fields.lockboxId || undefined,
      fileNumber: fields.fileNumber || undefined,
      policyNumber: fields.policyNumber || undefined,
      accountNumber: fields.accountNumber,
      orderId: fields.orderId,
      orderNumber: fields.orderNumber || undefined,
      orderStatus: fields.orderStatus,
      stateCode: fields.stateCode || undefined,
      invoiceNumber: fields.invoiceNumber ? fields.invoiceNumber.replace(/\D/g, '') || undefined : undefined,
      batchReferenceNumber: fields.batchReferenceNumber || undefined,
    };
    const token = await getAccessToken();
    const response = await getAllOrdersApi(payload, token);
    for (const order of response) {
      order.orderStatus = order.orderStatus?.replace('Bulk', '').trim();
    }
    return response;
  };

  const handleSearch = () => {
    const fields: SearchFields = {
      orderId,
      orderNumber,
      policyNumber,
      fileNumber,
      invoiceNumber,
      batchReferenceNumber,
      accountNumber,
      lockboxId: lockboxFilter,
      orderStatus,
      fromOrderDate,
      toOrderDate,
      stateCode,
    };
    const validation = validateSearchCombination(fields);
    if (!validation.valid) {
      if (validation.message) {
        addGlobalMsg({ message: validation.message, type: 'error' });
      }
      return;
    }
    setHasSearched(true);
    setSavedFilters(fields);
    executeGetAllOrders(fields);
  };

  const handleClearSearch = () => {
    setLockboxFilter(null);
    setOrderId(undefined);
    setOrderNumber('');
    setPolicyNumber('');
    setFileNumber('');
    setInvoiceNumber('');
    setBatchReferenceNumber('');
    setAccountNumber(undefined);
    setOrderStatus(undefined);
    setFromOrderDate('');
    setToOrderDate('');
    setStateCode('');
    setHasSearched(false);
    setSavedFilters(null);
    setAllOrders([]);
    setTotalFiles(0);
    setTotalNet(0);
  };

  const handleLockboxMenuOption = useCallback((value: number) => {
    setLockboxFilter(value === 0 ? null : value);
  }, []);

  const getLockboxList = async (): Promise<Array<LockboxNumberType>> => {
    if (lockbox) return lockbox;
    const token = await getAccessToken();
    const response = await getLockboxNumberApi(token);
    setLockbox(response);
    return response;
  };

  const fetchLockboxNumbers = async () => {
    const result = await getLockboxList();
    setLockboxList([{ name: 'All Lockboxes', value: 0 }, ...result]);
  };

  const goToOrderDetails = useCallback(
    async (row: AllOrdersItemType) => {
      const token = await getAccessToken();
      const firmDetailsResults = await getFirmDetails(token, String(row.firmId));
      if (firmDetailsResults) {
        window.localStorage.setItem('userFirm', JSON.stringify(firmDetailsResults));
        setUserFirm && setUserFirm(firmDetailsResults);
        const myOrder: OrderType = {
          firmId: row.firmId,
          lockboxId: row.lockboxId,
          orderId: row.orderId,
          orderStatus: row.orderStatus,
          orderDate: row.orderDate,
          referenceNumber: row.referenceNumber,
          checkCount: row.checkCount,
          reportedAmount: row.orderNet,
          targetTab: 'orderQueue',
          firm: {
            firmId: row.firmId,
            firmName: row.firmName,
            isActive: true,
          },
          lockboxNumber: {
            name: lockboxList.find((l) => l.value === row.lockboxId)?.name ?? '',
            value: row.lockboxId,
          },
        };
        const state = { order: myOrder, from: '/order-management/all-orders' };
        if (row.orderStatus === 'Closed') {
          history.push('/order-management/closed-order-details', state);
        } else {
          history.push('/order-management', state);
        }
      }
    },
    [getAccessToken, lockboxList, history, setUserFirm],
  );

  const {
    execute: executeGetAllOrders,
    status: executeGetAllOrdersStatus,
    value: getAllOrdersValue,
    errors: getAllOrdersErrors,
  } = useAsync<Array<AllOrdersItemType>>(getAllOrders, false);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: 'Order Status',
        cellRenderer: (params: ICellRendererParams) => {
          return <CellStatus status={params.value} colorsByStatus={colorsByStatus} />;
        },
        field: 'orderStatus',
        filter: 'agSetColumnFilter',
        minWidth: 166,
        maxWidth: 256,
        tooltipField: 'orderStatus',
        suppressFloatingFilterButton: false,
      },
      {
        headerName: 'Order ID',
        field: 'orderId',
        minWidth: 144,
        maxWidth: 256,
        tooltipField: 'orderId',
        cellRenderer: CellLink,
        cellRendererParams: (row: ValueFormatterParams) => {
          return row.data.orderStatus === 'Closed'
            ? { linkTemplate: 'order-management/closed-order-details' }
            : { linkTemplate: 'order-management' };
        },
        onCellClicked: (event: CellClickedEvent) => {
          goToOrderDetails(event.data);
        },
        sort: 'desc',
      },
      {
        headerName: 'Order Number',
        field: 'orderNumbers',
        minWidth: 144,
        tooltipField: 'orderNumbers',
        cellRenderer: (params: ICellRendererParams) => {
          return params.value.length > 0 ? params.value.join(', ') : '';
        },
      },
      {
        headerName: 'Firm',
        field: 'firmName',
        minWidth: 256,
        tooltipField: 'firmName',
      },
      {
        headerName: 'Batch/Ticket Reference Number',
        field: 'referenceNumber',
        minWidth: 144,
        maxWidth: 384,
        tooltipField: 'referenceNumber',
      },
      {
        headerName: 'Order Date',
        filter: 'agDateColumnFilter',
        filterParams: filterParams,
        field: 'orderDate',
        minWidth: 144,
        maxWidth: 256,
        cellRenderer: (params: ICellRendererParams) => {
          return params?.value?.length > 0 ? formatDate(new Date(params.value), 'MM/dd/yyyy') : '';
        },
        tooltipValueGetter: dateTooltipValue,
      },
      {
        headerName: 'Date Closed',
        filter: 'agDateColumnFilter',
        filterParams: filterParams,
        field: 'closedDate',
        minWidth: 144,
        maxWidth: 256,
        cellRenderer: (params: ICellRendererParams) => {
          return params?.value?.length > 0 ? formatDate(new Date(params.value), 'MM/dd/yyyy') : '-';
        },
        tooltipValueGetter: dateTooltipValue,
      },
      {
        headerName: 'Number of Files',
        type: 'rightAligned',
        field: 'fileCount',
        filter: 'agNumberColumnFilter',
        minWidth: 144,
        maxWidth: 256,
        tooltipField: 'fileCount',
      },
      {
        headerName: 'Order Net',
        type: 'rightAligned',
        field: 'orderNet',
        filter: 'agNumberColumnFilter',
        minWidth: 144,
        maxWidth: 256,
        tooltipField: 'orderNet',
        valueFormatter: numberValueFormatter,
      },
      {
        field: '',
        floatingFilterComponentParams: { suppressFilterButton: true },
        suppressHeaderFilterButton: true,
        floatingFilter: false,
        sortable: false,
        cellRenderer: CellMessage,
        onCellClicked: (event: CellClickedEvent) => {
          try {
            const payload: GetNotesActionLog = {
              orderId: event.data.orderId,
              fileId: '0',
            };
            setIsDashboardMessagePanelOpen(true);
            setConversationPayload(payload);
          } catch (e) {
            if (e instanceof Error) {
              addGlobalMsg({
                message: e.message,
                type: 'error',
              });
            }
          }
        },
        minWidth: 40,
        maxWidth: 40,
        cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
        tooltipValueGetter: () => 'Open conversation panel',
        pinned: 'right',
      },
    ],
    [goToOrderDetails],
  );

  const gridConfig = useMemo((): AgGridReactProps => {
    let sessionColumns: ColDef[];
    if (dashboardSessionColumnDefs) {
      sessionColumns = dashboardSessionColumnDefs.map((sessionColumn: ColDef) => {
        const colDef = columnDefs.find((c) => c.field === sessionColumn.field);
        if (colDef) {
          colDef.sort = sessionColumn.sort;
          return colDef;
        }
      });
    } else {
      sessionColumns = columnDefs;
    }
    return {
      rowData: allOrders ?? [],
      columnDefs: sessionColumns,
      maintainColumnOrder: true,
      gridOptions: {
        getRowId: (params: GetRowIdParams<AllOrdersItemType>) => String(params.data.orderId),
        suppressRowClickSelection: true,
      },
      defaultColDef: {
        editable: false,
        filter: 'agTextColumnFilter',
        flex: 1,
        floatingFilter: true,
        resizable: true,
        sortable: true,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        suppressSizeToFit: false,
        width: 144,
        minWidth: 144,
      },
      noRowsOverlayComponent: 'customOverlayNoRows',
      components: {
        customOverlayNoRows: CustomOverlayNoRows,
      },
      domLayout: 'autoHeight',
      onGridReady: onGridReady,
      onFilterChanged: onFilterChanged,
      onColumnMoved: (event) => {
        setDashboardSessionColumnDefs(event.api.getColumnDefs());
      },
      onGridColumnsChanged: (event) => {
        setDashboardSessionColumnDefs(event.api.getColumnDefs());
      },
      onSortChanged: (event) => {
        setDashboardSessionColumnDefs(event.api.getColumnDefs());
      },
    };
  }, [allOrders, dashboardSessionColumnDefs, columnDefs]);

  useEffect(() => {
    bookedOrder &&
      addSnackbarMessage({
        message: `Order ID ${bookedOrder.orderId} Booked`,
        type: 'success',
      });
    setBookedOrder(null);
    fetchLockboxNumbers();
    if (savedFilters) {
      setLockboxFilter(savedFilters.lockboxId ?? null);
      setOrderId(savedFilters.orderId);
      setOrderNumber(savedFilters.orderNumber ?? '');
      setPolicyNumber(savedFilters.policyNumber ?? '');
      setFileNumber(savedFilters.fileNumber ?? '');
      setInvoiceNumber(savedFilters.invoiceNumber ?? '');
      setBatchReferenceNumber(savedFilters.batchReferenceNumber ?? '');
      setAccountNumber(savedFilters.accountNumber);
      setOrderStatus(savedFilters.orderStatus);
      setFromOrderDate(savedFilters.fromOrderDate ?? '');
      setToOrderDate(savedFilters.toOrderDate ?? '');
      setStateCode(savedFilters.stateCode ?? '');
      const restoredFields: SearchFields = {
        orderId: savedFilters.orderId,
        orderNumber: savedFilters.orderNumber ?? '',
        policyNumber: savedFilters.policyNumber ?? '',
        fileNumber: savedFilters.fileNumber ?? '',
        invoiceNumber: savedFilters.invoiceNumber ?? '',
        batchReferenceNumber: savedFilters.batchReferenceNumber ?? '',
        accountNumber: savedFilters.accountNumber,
        lockboxId: savedFilters.lockboxId ?? null,
        orderStatus: savedFilters.orderStatus,
        fromOrderDate: savedFilters.fromOrderDate ?? '',
        toOrderDate: savedFilters.toOrderDate ?? '',
        stateCode: savedFilters.stateCode ?? '',
      };
      if (validateSearchCombination(restoredFields).valid) {
        setHasSearched(true);
        executeGetAllOrders(restoredFields);
      }
    }
  }, []);

  useEffect(() => {
    if (executeGetAllOrdersStatus === 'success') {
      setAllOrders(getAllOrdersValue ?? []);
      setTotalFiles(getAllOrdersValue?.reduce((acc, curr) => acc + curr.fileCount, 0) ?? 0);
      setTotalNet(getAllOrdersValue?.reduce((acc, curr) => acc + curr.orderNet, 0) ?? 0);
    }
    if (executeGetAllOrdersStatus === 'error') {
      getAllOrdersErrors?.map((err) => {
        addGlobalMsg({
          message:
            typeof err === 'string' && err !== '[object Object]'
              ? err
              : 'An error occurred while searching orders. Please try again.',
          type: 'error',
        });
      });
    }
  }, [executeGetAllOrdersStatus]);

  useEffect(() => {
    setDisplayedRowCount(getDisplayedRowCount());
  }, [displayedRowCount]);

  return (
    <>
      <ContentContainer fullWidth>
        <Content className={classes.root}>
          <div>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PageHeader
                  divider={useHeaderDivider !== undefined ? useHeaderDivider : true}
                  {...(showHeaderElipsis === undefined || showHeaderElipsis
                    ? {
                        menuItems: [
                          {
                            label: 'Knowledge Base',
                            link: KBLinks.home,
                          },
                        ],
                      }
                    : {})}
                  title={pageHeader || 'Order Management'}
                  contentRight={
                    <div style={{ display: 'flex' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name="Advanced Search"
                            checked={advancedSearch !== false}
                            onChange={() => {
                              setAdvancedSearch(advancedSearch !== false ? false : true);
                            }}
                          />
                        }
                        labelPlacement="start"
                        label={'Search Fields'}
                        data-testid="order-mgmt-all-orders-switch-advanced-search"
                      />
                    </div>
                  }
                />
              </Grid>
            </Grid>
            {advancedSearch !== false && (
              <Grid container spacing={2}>
                {/* Row 1: Order ID | Order Number | Order Status | Account Number */}
                <Grid item xs={6} sm={3}>
                  <AgentNetTextInput
                    fullWidth
                    data-testid={`${dataQaPrefix}-Advanced-Search-OrderId`}
                    label="Order ID"
                    value={orderId !== undefined ? String(orderId) : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setOrderId(v ? Number(v) : undefined);
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <AgentNetTextInput
                    fullWidth
                    data-testid={`${dataQaPrefix}-Advanced-Search-OrderNumber`}
                    label="Order Number"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value?.trim() ?? '')}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <AgentNetDropdownSelector
                    label="Order Status"
                    name="orderStatusFilter"
                    qaAttribute={`${dataQaPrefix}-Advanced-Search-Order-Status`}
                    options={[{ name: 'All Statuses', value: 0 }, ...orderStatusOptions]}
                    value={orderStatus ?? 0}
                    id="orderStatusFilter"
                    dropdowntype="outlined"
                    menuOption={(value) => {
                      setOrderStatus(value === 0 ? undefined : Number(value));
                      return false;
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <AgentNetTextInput
                    fullWidth
                    data-testid={`${dataQaPrefix}-Advanced-Search-AccountNumber`}
                    label="Account Number"
                    value={!Number.isNaN(accountNumber) ? String(accountNumber || '') : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setAccountNumber(v ? Number(v) : undefined);
                    }}
                  />
                </Grid>
                {/* Row 2: File Number | Invoice Number | Policy Number | State Code */}
                <Grid item xs={6} sm={3}>
                  <AgentNetTextInput
                    fullWidth
                    data-testid={`${dataQaPrefix}-Advanced-Search-FileNumber`}
                    label="File Number"
                    value={fileNumber}
                    onChange={(e) => setFileNumber(e.target.value?.trim() ?? '')}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <AgentNetTextInput
                    fullWidth
                    data-testid={`${dataQaPrefix}-Advanced-Search-InvoiceNumber`}
                    label="Invoice Number"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value?.trim() ?? '')}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <AgentNetTextInput
                    fullWidth
                    data-testid={`${dataQaPrefix}-Advanced-Search-PolicyNumber`}
                    label="Policy Number"
                    value={policyNumber}
                    onChange={(e) => setPolicyNumber(e.target.value?.trim() ?? '')}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <AgentNetDropdownSelector
                    label="State Code"
                    qaAttribute={`${dataQaPrefix}-Advanced-Search-StateCode`}
                    options={stateOptions}
                    value={stateCode}
                    dropdowntype="outlined"
                    menuOption={(value: string) => setStateCode(value)}
                  />
                </Grid>
                {/* Row 3: Lockbox | Batch/Ticket Reference Number | Order Date - From | Order Date - To */}
                <Grid item xs={6} sm={3}>
                  <AgentNetDropdownSelector
                    label="Lockbox"
                    name="advancedSearchlockboxFilter"
                    qaAttribute={`${dataQaPrefix}-Advanced-Search-Lockbox-Filter`}
                    options={lockboxList ?? []}
                    value={lockboxFilter ?? 0}
                    id="lockboxFilter"
                    dropdowntype="outlined"
                    menuOption={handleLockboxMenuOption}
                    prefixIcon={<LockboxIcon />}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <AgentNetTextInput
                    fullWidth
                    data-testid={`${dataQaPrefix}-Advanced-Search-BatchReferenceNumber`}
                    label="Batch/Ticket Reference Number"
                    value={batchReferenceNumber}
                    onChange={(e) => setBatchReferenceNumber(e.target.value?.trim() ?? '')}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <DateFieldString
                    qaAttribute={`${dataQaPrefix}-Advanced-Search-FromOrderDate`}
                    label="Order Date - From"
                    value={fromOrderDate}
                    onChange={(date) => setFromOrderDate(date ?? '')}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <DateFieldString
                    qaAttribute={`${dataQaPrefix}-Advanced-Search-ToOrderDate`}
                    label="Order Date - To"
                    value={toOrderDate}
                    onChange={(date) => setToOrderDate(date ?? '')}
                  />
                </Grid>
                {/* Row 4: Clear Search | Search */}
                <Grid item>
                  <AgentNetButton
                    color="primary"
                    data-testid="ClearSearch"
                    onClick={handleClearSearch}
                    variant="outlined"
                    id="clear-search"
                    size="small"
                  >
                    Clear Search
                  </AgentNetButton>
                </Grid>
                <Grid item>
                  <AgentNetButton
                    color="primary"
                    data-testid="Search"
                    variant="contained"
                    onClick={handleSearch}
                    disabled={!isSearchValid}
                    id="search"
                    size="small"
                  >
                    Search
                  </AgentNetButton>
                </Grid>
              </Grid>
            )}
            {hasSearched && executeGetAllOrdersStatus === 'pending' && (
              <Grid container>
                <Grid item xs={12}>
                  <CustomOverlayLoading />
                </Grid>
              </Grid>
            )}
            {hasSearched && getAllOrdersValue != null && executeGetAllOrdersStatus !== 'pending' && (
              <OrderResultsPanel
                gridConfig={gridConfig}
                orders={getAllOrdersValue}
                totalFiles={totalFiles}
                totalNet={totalNet}
                displayedRowCount={displayedRowCount}
              />
            )}
          </div>
        </Content>
      </ContentContainer>
      {conversationPayload && (
        <ConversationDrawer
          conversationPayload={conversationPayload}
          onClose={() => setConversationPayload(undefined)}
          isDashboardMessagePanelOpen={isDashboardMessagePanelOpen}
          readOnly={false}
        />
      )}
    </>
  );
};

export default Dashboard;
